import React from 'react';
import isNil from 'lodash.isnil';
import get from 'lodash.get';
import head from 'lodash.head';
import isEmpty from 'lodash.isempty';
import { format } from 'date-fns';
import { getUrlParams } from './urlUtils';
import { getAccuracy } from '../../features/studentSummary/shared/trcSummary/trcContent/trcTables/TableRow/tableRowUtils';
import {
    getTrcReadingCompScoreWithLevel,
    getWrittenComp,
    getTrcCardStats,
} from '../../features/studentSummary/shared/trcSummary/trcSummaryUtils';
import {
    PERFORMANCE_LEVELS,
    TRC_SCORE,
    SPECIAL_SCORE_TITLE_MAPPER,
    BOOK_GENRES,
    CLASSNAMES,
    LABEL,
    BOOK_TYPE,
    KEY,
} from '../../constants/shared/TrcConstants';
import { MEASURE } from '../../constants/shared/MeasuresConstants';
import { DATE_FORMAT, NA } from '../../constants/shared/CommonConstants';

const { TRC } = MEASURE;
const { PS } = TRC_SCORE;
const { FICTION } = BOOK_TYPE;
const { RETELL, RECALL, RETELL_OR_RECALL, RETELLING, NON_FICTION } = LABEL;
const { RUBRIC_TYPE, QUESTIONS_TYPE } = KEY;

export const getPerformanceLevelAbbr = key =>
    get(
        PERFORMANCE_LEVELS.find(pl => pl.key === key),
        'abbreviation',
        '',
    );

export const getPerformanceLevelLabel = key =>
    get(
        PERFORMANCE_LEVELS.find(pl => pl.key === key),
        'label',
        '',
    );

export const isTrcPresumedResult = (measure, score) =>
    !isNil(score) && measure === TRC && score.bookType === PS;

export const getTrcResultsWithoutDetails = (results = []) =>
    results.filter(result => result.measure === TRC && isEmpty(result.itemLevelDetails));

export const getPerformanceLevelTitle = key =>
    get(
        PERFORMANCE_LEVELS.find(pl => pl.key === key),
        'title',
        '',
    );

export const getGenreTitle = key =>
    get(
        BOOK_GENRES.find(genre => genre.key === key),
        'title',
        NON_FICTION,
    );

export const isIncorrectWord = ({ inserted, told, substituted, omitted }) =>
    inserted || told || substituted || omitted;

export const isWordRepeated = (word, nextWord) =>
    word.repetitionContinued ||
    word.repetitionEnded ||
    (word.inserted &&
        nextWord.insertedWord &&
        (nextWord.repetitionContinued || nextWord.repetitionEnded));

export const getRepeatedWordClassName = (word, nextWord) =>
    isWordRepeated(word, nextWord) ? CLASSNAMES.REPEAT_CENTER_ICON_CLASS : '';

export const getRetellTitle = (retellRecall, atlasValue, bookType) => {
    if (retellRecall) {
        return RETELL_OR_RECALL;
    }
    if (atlasValue) {
        return bookType === FICTION ? RETELL : RECALL;
    }
    return RETELLING;
};

export const getOralComprehensionValue = (trcScore, probeDetailResult) => {
    const { oralCompResult } = probeDetailResult;
    if (isNil(oralCompResult)) return NA;
    switch (oralCompResult.oralCompType) {
        case RUBRIC_TYPE:
            return `${oralCompResult.averageScore}/${oralCompResult.averageMaxScore}`;
        case QUESTIONS_TYPE:
            return `${trcScore.oralComprehension.actualResult}/${trcScore.oralComprehension.maxResult}`;
        default:
            return NA;
    }
};

export const getWrittenComprehensionValue = resultGroup => {
    if (resultGroup) {
        const { results, proficient } = resultGroup;
        if (results.length === 1) {
            const { score, maxScore } = head(results);
            return `${score}/${maxScore}`;
        }
        return `${proficient ? 'Prof.' : 'Not Prof.'}`;
    }
    return NA;
};

export const getBookItem = ({ bookUid, actualScore, bookTitle, bookType }) => {
    const specialTitle = SPECIAL_SCORE_TITLE_MAPPER[actualScore];
    const bookTypeFormatted = bookType ? `(${getGenreTitle(bookType)})` : '';
    return {
        uid: bookUid,
        name: (
            <>
                <span>Level {actualScore}</span>
                <span className="print-book-title">{bookTitle}</span>
            </>
        ),
        title: specialTitle ? `${specialTitle}: ${bookTitle}` : `${bookTitle} ${bookTypeFormatted}`,
    };
};

export const findBookResult = (bookResults, bookId, resultId) =>
    bookResults.find(({ trcScore }) =>
        bookId ? trcScore.bookUid === bookId : trcScore.resultSid === resultId,
    );

export const getAssessmentDate = (result, location) => {
    const { bookId } = getUrlParams(location);
    const { measure, itemLevelDetails, resultProbeUid, date } = result;
    let assessmentDate;
    if (measure === TRC) {
        if (isEmpty(itemLevelDetails)) return null;
        const bookResult = itemLevelDetails.find(({ trcScore }) =>
            bookId ? bookId === trcScore.bookUid : resultProbeUid === trcScore.resultSid,
        );
        assessmentDate = get(bookResult, 'trcScore.stringDateCompleted');
    } else {
        assessmentDate = date;
    }

    return isEmpty(assessmentDate) ? null : format(new Date(assessmentDate), DATE_FORMAT.FULL_DATE);
};

export const getScoreLabel = (score, maxScore) => `Score: ${score}/${maxScore}`;

export const getFinalTrcResult = (results = [], resultId = '') =>
    get(
        results.find(result => result.probeDetailResult.resultProbeUid === resultId),
        'trcScore',
        {},
    );

export const buildTrcObject = trcScore => {
    const {
        bookUid: bookId,
        complete,
        presumedResult,
        performanceLevel,
        accuracy,
        oralComprehension,
        writtenComprehensionResultGroup,
    } = trcScore;
    const isWritCompComplete = get(writtenComprehensionResultGroup, 'complete');
    const writtenComp = isWritCompComplete ? head(writtenComprehensionResultGroup.results) : null;
    const accuracyResult = presumedResult ? null : accuracy;
    const performanceLevelTitle = getPerformanceLevelTitle(performanceLevel);
    const performance = complete ? performanceLevelTitle : 'In progress';
    return {
        ...trcScore,
        bookId,
        isComplete: complete,
        performance,
        accuracy: accuracyResult ? getAccuracy(accuracy) : NA,
        readingComp: getTrcReadingCompScoreWithLevel(oralComprehension),
        writtenComp,
        correctWrittenComp: getWrittenComp,
        cardStats: getTrcCardStats(
            performanceLevelTitle,
            accuracyResult,
            oralComprehension,
            writtenComp,
        ),
    };
};
